import { useLoaderData } from "@remix-run/react";
import * as routeUtils from "~/contentful/route-utils";
import { json, LoaderFunction } from "@remix-run/server-runtime";
import invariant from "tiny-invariant";
import {
  INewsletter,
  ITopic,
  SpecificLocale,
} from "~/@types/generated/contentful";
import { getEntryBySlug, getNewsletter } from "~/contentful/index.server";
import { i18n } from "~/i18n.server";
import {
  ComponentDataProvider,
  PageComponentData,
} from "~/contexts/ComponentDataProvider";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import React, { useEffect } from "react";
import { pageView, track } from "~/components/utils/gtm.client";
import { ClientSide } from "~/@types";
import { AlgoliaProvider } from "~/components/search";
import { ALGOLIA_INDICES } from "~/config";
import FilteredInsightsList from "~/components/Insight/FilteredInsightsList";
import {
  InstantSearchServerState,
  InstantSearchSSRProvider,
  useHits,
} from "react-instantsearch-hooks-web";
import { getServerState } from "react-instantsearch-hooks-server";
import { createMemoryRouter } from "react-router-dom";
import { renderToString } from "react-dom/server";
import { getTopicsWithTotals } from "~/contexts/dataUtils/getTopicsWithTotals";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  SchemaOrgType,
  tbiOrganizationSchema,
} from "~/components/utils/schemaOrgUtils.server";
import { Newsletter } from "~/components/shared";
import { TopicHero } from "~/components/heros";
import RootProvider from "~/contexts/RootProvider";
import { getActiveExperts } from "~/contexts/dataUtils/getActiveExperts";
import {
  createStaticHandler,
  StaticRouterProvider,
} from "react-router-dom/server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("topic", "insights");

interface LoaderData {
  newsletter: ClientSide<INewsletter> | undefined;
  topic: ClientSide<ITopic>;
  componentData: PageComponentData;
  meta: routeUtils.MetaInfo;
  url: string;
  serverState?: InstantSearchServerState;
  schemaOrg: SchemaOrgType;
}

export const loader: LoaderFunction = async ({ params, request, context }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.topicSlug;

  invariant(slugToQuery, invariantMessage);

  const topic = (await getEntryBySlug("topic", slugToQuery, {
    locale,
  })) as SpecificLocale<ITopic>;
  if (params.topicSlug && !topic) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Topic Not Found",
        backgroundColor: "black",
        notFound: "topic",
      } as FourOFourCaughtData),
      { status: 404 }
    );
  }

  const topics = await getTopicsWithTotals(locale);
  // Get experts for the expert carousel that opens when you click the experts on a card
  const experts = await getActiveExperts(locale);

  let componentData: PageComponentData = {
    topics,
    experts,
  };

  const newsletter = await getNewsletter(locale);
  if (!newsletter) {
    console.warn("No newsletter found");
  }

  const schemaUrl = new URL(request.url);
  schemaUrl.search = "";
  schemaUrl.hash = "";

  const schemaOrg: SchemaOrgType = {
    "@context": "https://schema.org",
    "@type": "Collection",
    about: {
      name: topic.fields.title,
      alternateName: topic.fields.slug,
      description: `Listing page for insights under the topic "${topic.fields.title}"`,
    },
    isAccessibleForFree: true,
    image: topic.fields.image.fields.image?.fields?.file?.url,
    url: schemaUrl.toString(),
    collectionSize: topics.find((t) => t.sys.id === topic.sys.id)?.total,
    publisher: tbiOrganizationSchema,
  };

  const searchParams = new URLSearchParams({
    fm: "jpg",
  });

  const openGraphImageUrl = `https:${
    topic.fields.image.fields.image?.fields?.file?.url
  }?${searchParams.toString()}`;

  const loaderData: LoaderData = {
    newsletter,
    topic,
    componentData,
    url: request.url,
    meta: routeUtils.getMetaInfo({
      title: topic.fields.title,
      description: `Insights related to ${topic.fields.title}`,
      openGraphImageUrl,
      requestUrl: request.url,
    }),
    schemaOrg,
  };

  const routes = [
    {
      id: "ID",
      path: "*",
      element: (
        <RootProvider>
          <TopicPageWrapper
            url={request.url}
            topic={topic}
            ip={context.ip as string | undefined}
          />
        </RootProvider>
      ),
      loader: () => json(loaderData),
    },
  ];

  // I would like to explain this well but I cannot.
  // It allows algolia components to render server-side.
  // https://reactrouter.com/en/main/routers/picking-a-router#using-v64-data-apis
  const router = createMemoryRouter(routes, {
    initialEntries: [new URL(request.url)],
    hydrationData: { loaderData: { ID: loaderData } },
  });

  // https://reactrouter.com/en/main/routers/static-router-provider
  const staticHandler = createStaticHandler(routes);
  const fetchRequest = new Request(request.url, { headers: request.headers });
  const staticContext = await staticHandler.query(fetchRequest);

  if (staticContext instanceof Response) {
    throw staticContext; // if there's an error or redirect
  }

  const serverState = await getServerState(
    <React.StrictMode>
      <StaticRouterProvider context={staticContext} router={router} />
    </React.StrictMode>,
    { renderToString }
  );

  return { ...loaderData, serverState };
};

export function TopicPage() {
  const { newsletter, topic, componentData, schemaOrg } =
    useLoaderData<LoaderData>();
  const { results } = useHits();

  useEffect(() => {
    track(pageView({ category: "Topic_listing", topic: topic.fields.title }));
  }, [topic.fields.title]);

  return (
    <ComponentDataProvider componentData={componentData}>
      <SchemaOrg content={schemaOrg} />
      {componentData.topics?.map((t) => {
        if (t.sys.id !== topic.sys.id) return null;
        return (
          <TopicHero key={t.sys.id} topic={topic} total={results?.nbHits} />
        );
      })}
      <FilteredInsightsList topic={topic} />
      {newsletter?.fields && <Newsletter fields={newsletter.fields} />}
    </ComponentDataProvider>
  );
}

export function TopicPageWrapper({
  serverState,
  url,
  topic,
  ip,
}: Pick<LoaderData, "serverState" | "url" | "topic"> & {
  ip?: string | undefined;
}) {
  return (
    <InstantSearchSSRProvider {...serverState}>
      <AlgoliaProvider
        url={url}
        indexName={ALGOLIA_INDICES.allDateDesc}
        currentTopicName={topic.fields.title}
        xForwardedFor={ip}
      >
        <TopicPage />
      </AlgoliaProvider>
    </InstantSearchSSRProvider>
  );
}

export default function AlgoWrapper() {
  const { serverState, url, topic } = useLoaderData<LoaderData>();
  return <TopicPageWrapper serverState={serverState} url={url} topic={topic} />;
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const CatchBoundary = routeUtils.ContentfulCatchBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
